/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { GetNodeByIdResponse, Node, NodeConfigMode } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMessage } from '@/hooks/message';
import { useAssignConfigRevisionMutation } from '@/hooks/mutation/useAssignConfigRevisionMutation';
import { useCancelScheduledAssignmentMutation } from '@/hooks/mutation/useCancelScheduledAssignmentMutation';
import { useCreateAuditConfigMutation } from '@/hooks/mutation/useCreateAuditConfigMutation';
import { useEnableMaintenanceModeMutation } from '@/hooks/mutation/useEnableMaintenanceModeMutation';
import { useNodeConfigHistoryQuery } from '@/hooks/query/useNodeConfigHistoryQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UseMaintenanceModeProps = {
  nodeId: string;
  node?: Node;
};

export type NodeMode = 'off' | 'maintenance' | 'audit';

export type UseMaintenanceModeReturn = {
  mode: NodeMode;
  isPending: boolean;
  isTransitioning: boolean;
  transitioningFromMode: NodeMode | null;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  startMaintenance: () => Promise<void>;
  endMaintenance: () => void;
  startTime?: string;
  isInteractionBlocked: boolean;
  isVersionMissing: boolean;
  // Audit mode properties
  isExitMaintenanceModalOpen: boolean;
  openExitMaintenanceModal: () => void;
  closeExitMaintenanceModal: () => void;
  enterAuditMode: (durationHours?: number) => Promise<void>;
  skipAuditAndEndMaintenance: () => Promise<void>;
  endAuditMode: () => Promise<void>;
  scheduledExitTime?: string;
};

function getNodeMode(node?: Node): NodeMode {
  const configMode = node?.hasRunningJob
    ? node.requestedRevisionState?.nodeConfig?.mode
    : node?.appliedRevisionState?.nodeConfig?.mode;

  if (configMode === 'maintenance') return 'maintenance';
  if (configMode === 'audit') return 'audit';
  return 'off';
}

/**
 * Hook that encapsulates all maintenance and audit mode state and handlers.
 * Simplifies prop drilling by providing a single object with all mode-related functionality.
 */
export function useMaintenanceMode({ nodeId, node }: UseMaintenanceModeProps): UseMaintenanceModeReturn {
  const { t } = useMimicTranslation('node');
  const [message] = useMessage();
  const queryClient = useQueryClient();
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const [mode, setMode] = useState<NodeMode>(() => getNodeMode(node));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExitMaintenanceModalOpen, setIsExitMaintenanceModalOpen] = useState(false);
  const [targetMode, setTargetMode] = useState<NodeMode | null>(null);
  // Track the mode we're transitioning from to maintain consistent UI during transitions
  const [transitioningFromMode, setTransitioningFromMode] = useState<NodeMode | null>(null);
  // Track the scheduled exit time for audit mode auto-exit
  const [scheduledExitTime, setScheduledExitTime] = useState<string | undefined>(undefined);
  // Track the scheduled job ID for cancellation when manually exiting audit mode
  const [scheduledJobId, setScheduledJobId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const currentMode = getNodeMode(node);
    setMode(currentMode);
    if (targetMode !== null && currentMode === targetMode && !node?.hasRunningJob) {
      setTargetMode(null);
      setTransitioningFromMode(null);
    }
  }, [node, targetMode]);

  const { mutateAsync: enableMaintenanceModeAsync, isPending: isMaintenancePending } = useEnableMaintenanceModeMutation(
    {
      onError: () => {
        setTargetMode(null);
        setTransitioningFromMode(null);
        message.error(t('maintenanceModeChangeFailed'));
      },
    },
  );

  const { mutateAsync: createAuditConfig, isPending: isAuditConfigPending } = useCreateAuditConfigMutation({
    onError: () => {
      setTargetMode(null);
      setTransitioningFromMode(null);
      message.error(t('auditModeEntryFailed'));
    },
  });

  const { mutateAsync: assignConfigAsync } = useAssignConfigRevisionMutation({});

  // Separate mutation for scheduling the auto-exit assignment
  const { mutateAsync: scheduleAssignment } = useAssignConfigRevisionMutation({});

  const { mutateAsync: cancelScheduledAssignment } = useCancelScheduledAssignmentMutation();

  // Fetch config history to find the last standard mode config (used when exiting maintenance/audit mode)
  const { data: configHistoryData, isLoading: isConfigHistoryLoading } = useNodeConfigHistoryQuery({
    nodeId,
    filters: { mode: 'standard' },
    enabled: mode === 'audit' || mode === 'maintenance',
  });

  // Get the last standard mode config from history
  const lastStandardConfig = useMemo(() => {
    if (!configHistoryData?.data?.length) return undefined;
    // The first entry should be the most recent standard config
    const entry = configHistoryData.data[0];
    return {
      configId: entry.nodeConfig.id,
      revisionNumber: entry.configRevision.revisionNumber,
    };
  }, [configHistoryData]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const openExitMaintenanceModal = () => setIsExitMaintenanceModalOpen(true);
  const closeExitMaintenanceModal = () => setIsExitMaintenanceModalOpen(false);

  // Poll the node until hasRunningJob becomes false, then verify the applied config mode.
  const POLL_INTERVAL_MS = 2_000;
  const POLL_TIMEOUT_MS = 60_000;

  const waitForJobCompletion = async (
    expectedMode: NodeConfigMode,
    deadline: number = Date.now() + POLL_TIMEOUT_MS,
  ): Promise<void> => {
    if (Date.now() >= deadline) {
      throw new Error('Job timed out');
    }

    const result = await queryClient.fetchQuery<GetNodeByIdResponse>({
      queryKey: ['node', selectedTenantId, nodeId],
      queryFn: () => mgmtPlaneAPIClient.nodes.getNodeById({ id: nodeId, tenantId: selectedTenantId! }),
      staleTime: 0,
    });

    const nodeData = result.data;

    if (!nodeData.hasRunningJob) {
      if (nodeData.appliedRevisionState?.nodeConfig?.mode === expectedMode) {
        return;
      }
      throw new Error('Job failed');
    }

    await new Promise<void>((resolve) => {
      setTimeout(resolve, POLL_INTERVAL_MS);
    });

    await waitForJobCompletion(expectedMode, deadline);
  };

  // Enter maintenance mode: enable → assign → poll
  const startMaintenance = async () => {
    closeModal();

    if (!node?.nodeVersion) {
      message.error(t('maintenanceModeUnsupported'));
      return;
    }

    setTransitioningFromMode(mode);
    setTargetMode('maintenance');

    try {
      // Step 1: Enable maintenance mode to get the config ID
      const result = await enableMaintenanceModeAsync({ nodeId });
      const { maintenanceConfigId } = result.data;

      // Step 2: Assign the maintenance config to the node
      await assignConfigAsync({
        nodeIDs: [nodeId],
        configID: maintenanceConfigId,
        revisionNumber: 1,
      });

      // Step 3: Wait for the job to complete
      await waitForJobCompletion('maintenance');
      message.success(t('maintenanceModeChangeRequested'));
    } catch (err) {
      setTargetMode(null);
      setTransitioningFromMode(null);

      if (err instanceof Error && (err.message === 'Job timed out' || err.message === 'Job failed')) {
        message.error(t('maintenanceModeChangeFailed'));
      }
      // Other errors are already handled by mutation onError callbacks
    }
  };

  // Opens the exit maintenance modal instead of directly ending maintenance
  const endMaintenance = () => {
    openExitMaintenanceModal();
  };

  // Skip audit and end maintenance mode directly by assigning the last standard config
  const skipAuditAndEndMaintenance = async () => {
    closeExitMaintenanceModal();

    if (!lastStandardConfig) {
      message.error(t('maintenanceModeExitFailed'));
      return;
    }

    setTransitioningFromMode(mode);
    setTargetMode('off');

    try {
      await assignConfigAsync({
        nodeIDs: [nodeId],
        configID: lastStandardConfig.configId,
        revisionNumber: lastStandardConfig.revisionNumber,
      });

      message.success(t('maintenanceModeChangeRequested'));
    } catch {
      setTargetMode(null);
      setTransitioningFromMode(null);
      message.error(t('maintenanceModeExitFailed'));
    }
  };

  // Enter audit mode by creating an audit config and assigning it to the node
  const enterAuditMode = async (durationHours: number = 48) => {
    if (!lastStandardConfig) {
      message.error(t('auditModeError.missingPreviousConfig'));
      return;
    }

    const { configId, revisionNumber } = lastStandardConfig;

    closeExitMaintenanceModal();
    setTransitioningFromMode(mode);
    setTargetMode('audit');

    // Calculate scheduled exit time
    const exitTime = new Date();
    exitTime.setHours(exitTime.getHours() + durationHours);
    const exitTimeISO = exitTime.toISOString();

    try {
      // Step 1: Create audit config from the last standard config
      const result = await createAuditConfig({
        configId,
        revisionNumber,
      });

      // Step 2: Assign the audit config to the node and wait for the job to complete
      await assignConfigAsync({
        nodeIDs: [nodeId],
        configID: result.data.auditConfigId,
        revisionNumber: 1, // Audit configs start at revision 1
      });

      await waitForJobCompletion('audit');
      message.success(t('auditModeEntered'));

      // Step 3: Schedule the source config to be re-assigned after duration.
      // Job completion is confirmed, so no retry loop is needed.
      try {
        const scheduleResponse = await scheduleAssignment({
          nodeIDs: [nodeId],
          configID: configId,
          revisionNumber,
          assignAt: exitTimeISO,
          jobName: 'Scheduled Audit Mode Exit',
        });

        if (scheduleResponse) {
          setScheduledExitTime(exitTimeISO);
          if (scheduleResponse.data.jobId) {
            setScheduledJobId(scheduleResponse.data.jobId);
          }
        }
      } catch {
        // Show warning but proceed - user will need to manually exit audit mode
        message.warning(t('auditModeScheduleWarning'));
        setScheduledExitTime(undefined);
        setScheduledJobId(undefined);
      }
    } catch (err) {
      setTargetMode(null);
      setTransitioningFromMode(null);
      setScheduledExitTime(undefined);
      setScheduledJobId(undefined);

      if (err instanceof Error && (err.message === 'Job timed out' || err.message === 'Job failed')) {
        message.error(t('auditModeError.jobTimeout'));
      }
      // Other errors are already handled by mutation onError callbacks
    }
  };

  // End audit mode by assigning the last standard config
  const endAuditMode = async () => {
    if (!lastStandardConfig) {
      message.error(t('auditModeError.missingPreviousConfig'));
      return;
    }

    setTransitioningFromMode(mode);
    setTargetMode('off');

    // Cancel the scheduled auto-exit job if it exists
    if (scheduledJobId) {
      try {
        await cancelScheduledAssignment({ scheduledJobId });
      } catch {
        // Silently fail - the job may have already been executed or cancelled
      }

      setScheduledJobId(undefined);
      setScheduledExitTime(undefined);
    }

    try {
      await assignConfigAsync({
        nodeIDs: [nodeId],
        configID: lastStandardConfig.configId,
        revisionNumber: lastStandardConfig.revisionNumber,
      });

      message.success(t('maintenanceModeChangeRequested'));
    } catch {
      setTargetMode(null);
      setTransitioningFromMode(null);
      message.error(t('maintenanceModeExitFailed'));
    }
  };

  const isInteractionBlocked = !!node?.hasRunningJob;
  const isVersionMissing = !node?.nodeVersion;
  const isTransitioning = targetMode !== null;
  const startTime = node?.appliedRevisionState?.statusDate;
  // Include config history loading in pending state so exit buttons are disabled while loading
  const isPending =
    isMaintenancePending ||
    isAuditConfigPending ||
    ((mode === 'audit' || mode === 'maintenance') && isConfigHistoryLoading);

  return {
    mode,
    isPending,
    isTransitioning,
    transitioningFromMode,
    isModalOpen,
    openModal,
    closeModal,
    startMaintenance,
    endMaintenance,
    startTime,
    isInteractionBlocked,
    isVersionMissing,
    // Audit mode properties
    isExitMaintenanceModalOpen,
    openExitMaintenanceModal,
    closeExitMaintenanceModal,
    enterAuditMode,
    skipAuditAndEndMaintenance,
    endAuditMode,
    scheduledExitTime,
  };
}
